import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, Settings2, UserRound } from "lucide-react";
import { cn } from "@discordforge/ui";
import { useMail } from "../MailApp";
import { GeneralTab } from "./GeneralTab";
import { IdentityTab } from "./IdentityTab";

type Tab = "general" | "identity";

const TABS: { id: Tab; label: string; icon: typeof Settings2 }[] = [
    { id: "general", label: "General", icon: Settings2 },
    { id: "identity", label: "Identity & signature", icon: UserRound },
];

export function SettingsApp() {
    const { loginData, onLogout } = useMail();
    const navigate = useNavigate();
    const [tab, setTab] = useState<Tab>("general");

    return (
        <div className="flex h-screen overflow-hidden bg-[#0b0b0f] text-white">
            {/* Sidebar */}
            <aside className="flex w-64 shrink-0 flex-col border-r border-white/10 bg-[#0a0a0e]">
                <div className="flex items-center gap-2.5 border-b border-white/10 px-5 py-4">
                    <div className="flex h-9 w-9 items-center justify-center rounded-xl bg-gradient-to-br from-orange-500 to-amber-500 text-sm font-black text-white shadow-[0_0_20px_rgba(249,115,22,0.25)]">
                        ⚙
                    </div>
                    <div className="min-w-0">
                        <div className="text-sm font-black tracking-tight">Settings</div>
                        <div className="truncate text-[11px] text-zinc-500">{loginData.Email}</div>
                    </div>
                </div>

                <nav className="flex flex-col gap-1 p-3">
                    {TABS.map(({ id, label, icon: Icon }) => (
                        <button
                            key={id}
                            type="button"
                            onClick={() => setTab(id)}
                            className={cn(
                                "flex cursor-pointer items-center gap-2.5 rounded-xl px-3.5 py-2.5 text-left text-[13px] font-semibold transition-colors",
                                tab === id
                                    ? "bg-orange-500/10 text-orange-400"
                                    : "text-zinc-400 hover:bg-white/5 hover:text-white"
                            )}
                        >
                            <Icon size={15} className={tab === id ? "text-orange-400" : "text-zinc-500"} />
                            {label}
                        </button>
                    ))}
                </nav>

                <div className="mt-auto flex flex-col gap-1 border-t border-white/10 p-3">
                    <button
                        type="button"
                        onClick={() => navigate("/")}
                        className="flex cursor-pointer items-center gap-2.5 rounded-xl px-3.5 py-2.5 text-left text-[13px] font-semibold text-zinc-400 transition-colors hover:bg-white/5 hover:text-white"
                    >
                        <ArrowLeft size={15} className="text-zinc-500" />
                        Back to mail
                    </button>
                    <button
                        type="button"
                        onClick={onLogout}
                        className="flex cursor-pointer items-center gap-2.5 rounded-xl px-3.5 py-2.5 text-left text-[13px] font-semibold text-zinc-400 transition-colors hover:bg-red-500/10 hover:text-red-400"
                    >
                        <span className="text-[15px] leading-none">⎋</span>
                        Sign out
                    </button>
                </div>
            </aside>

            {/* Content */}
            <main className="custom-scrollbar min-w-0 flex-1 overflow-y-auto">
                <div className="mx-auto max-w-3xl px-8 py-8">
                    <header className="mb-8">
                        <h1 className="text-xl font-black tracking-tight">
                            {tab === "general" ? "General settings" : "Identity & signature"}
                        </h1>
                        <p className="mt-1 text-sm text-zinc-500">
                            {tab === "general"
                                ? "List size, auto-logout, notifications and image handling."
                                : "How you present yourself when composing mail — including your signature."}
                        </p>
                    </header>
                    {tab === "general" ? <GeneralTab /> : <IdentityTab />}
                </div>
            </main>
        </div>
    );
}
